import React, { useEffect, useState } from "react";
import client, { formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Copy, Link2, CheckCircle2 } from "lucide-react";

export default function AdminInvites() {
    const [list, setList] = useState([]);
    const [show, setShow] = useState(false);
    const [form, setForm] = useState({ note: "", max_uses: 1 });
    const [copied, setCopied] = useState(null);

    const load = () => client.get("/admin/invites").then((r) => setList(r.data));
    useEffect(() => {
        load();
    }, []);

    const create = async (e) => {
        e.preventDefault();
        try {
            await client.post("/admin/invites", form);
            toast.success("Invite created");
            setForm({ note: "", max_uses: 1 });
            setShow(false);
            load();
        } catch (err) {
            toast.error(formatErr(err));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this invite?")) return;
        await client.delete(`/admin/invites/${id}`);
        toast.success("Deleted");
        load();
    };

    const copyText = (txt, key) => {
        navigator.clipboard.writeText(txt);
        setCopied(key);
        setTimeout(() => setCopied(null), 1500);
        toast.success("Copied");
    };

    const inviteLink = (code) => `${window.location.origin}/register?invite=${code}`;

    return (
        <div>
            <div className="flex items-end justify-between">
                <div>
                    <span className="label-cap text-xs text-neon-orange">Invite-Only Access</span>
                    <h1 className="font-display font-black text-4xl uppercase tracking-tight mt-1">Invites</h1>
                </div>
                <button
                    onClick={() => setShow(!show)}
                    data-testid="adm-new-invite"
                    className="btn-neon px-4 py-2 flex items-center gap-2"
                >
                    <Plus size={16} /> Issue
                </button>
            </div>

            <p className="text-zinc-400 text-sm mt-2">
                NoExcuse is invite-only. Issue codes or shareable links to vetted athletes.
            </p>

            {show && (
                <form onSubmit={create} className="mt-6 surface-2 p-4 grid gap-3" data-testid="adm-invite-form">
                    <div>
                        <label className="label-cap text-[10px] text-zinc-500">Note (who/why)</label>
                        <input
                            data-testid="if-note"
                            value={form.note}
                            onChange={(e) => setForm({ ...form, note: e.target.value })}
                            placeholder="Beta tester · friend of admin · etc."
                            className={inp + " mt-1"}
                        />
                    </div>
                    <div>
                        <label className="label-cap text-[10px] text-zinc-500">Max Uses</label>
                        <input
                            type="number"
                            min={1}
                            max={500}
                            data-testid="if-max"
                            value={form.max_uses}
                            onChange={(e) => setForm({ ...form, max_uses: +e.target.value })}
                            className={inp + " mt-1"}
                        />
                    </div>
                    <button type="submit" data-testid="if-submit" className="btn-primary py-3">
                        Generate Code
                    </button>
                </form>
            )}

            <div className="mt-6 space-y-2">
                {list.length === 0 ? (
                    <div className="surface-1 p-6 text-center text-zinc-400">No invites yet. Issue your first.</div>
                ) : (
                    list.map((inv) => {
                        const exhausted = inv.uses >= inv.max_uses;
                        return (
                            <div
                                key={inv.id}
                                data-testid={`adm-invite-${inv.id}`}
                                className={`surface-1 p-4 ${exhausted ? "opacity-60" : ""}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <code className="font-mono font-bold text-base text-neon-green tracking-widest">
                                                {inv.code}
                                            </code>
                                            {exhausted && (
                                                <span className="label-cap text-[10px] text-zinc-500 px-2 py-0.5 surface-3">
                                                    Used Up
                                                </span>
                                            )}
                                        </div>
                                        {inv.note && <div className="text-xs text-zinc-400 mt-1">{inv.note}</div>}
                                        <div className="text-[10px] text-zinc-500 mt-1 label-cap">
                                            {inv.uses} / {inv.max_uses} used · by {inv.created_by}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => remove(inv.id)}
                                        data-testid={`adm-invite-del-${inv.id}`}
                                        className="w-8 h-8 surface-3 flex items-center justify-center hover:border-neon-orange shrink-0"
                                    >
                                        <Trash2 size={14} className="text-neon-orange" />
                                    </button>
                                </div>
                                <div className="mt-3 grid grid-cols-2 gap-2">
                                    <button
                                        onClick={() => copyText(inv.code, `code-${inv.id}`)}
                                        data-testid={`adm-invite-copy-${inv.id}`}
                                        className="surface-3 px-3 py-2 label-cap text-[10px] flex items-center justify-center gap-1 hover:border-neon-green"
                                    >
                                        {copied === `code-${inv.id}` ? (
                                            <CheckCircle2 size={12} className="text-neon-green" />
                                        ) : (
                                            <Copy size={12} />
                                        )}
                                        Copy Code
                                    </button>
                                    <button
                                        onClick={() => copyText(inviteLink(inv.code), `link-${inv.id}`)}
                                        data-testid={`adm-invite-link-${inv.id}`}
                                        className="surface-3 px-3 py-2 label-cap text-[10px] flex items-center justify-center gap-1 hover:border-neon-orange"
                                    >
                                        {copied === `link-${inv.id}` ? (
                                            <CheckCircle2 size={12} className="text-neon-green" />
                                        ) : (
                                            <Link2 size={12} />
                                        )}
                                        Copy Link
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

const inp = "w-full bg-charcoal-700 border border-charcoal-500 px-3 py-2 text-white text-sm focus:border-neon-orange focus:outline-none";
